import type { FC } from 'react';
import { Ban, Eye, ListChecks } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { arr, optStr, str } from '@/app/lib/a2ui/props.ts';

const messages = defineMessages({
  toDo: {
    id: 'fieldTasks.toDo',
    defaultMessage: 'Field work',
    description: 'Heading above the list of things to do in the field at this growth stage.',
  },
  avoid: {
    id: 'fieldTasks.avoid',
    defaultMessage: 'Hold off on',
    description: 'Heading above the list of things the farmer should not do right now.',
  },
  watch: {
    id: 'fieldTasks.watch',
    defaultMessage: 'Watch for',
    description: 'Heading above the problem most likely to appear at this stage.',
  },
  priorityNow: {
    id: 'fieldTasks.priority.now',
    defaultMessage: 'Now',
    description: 'Priority tag: do this today or tomorrow.',
  },
  priorityWeek: {
    id: 'fieldTasks.priority.week',
    defaultMessage: 'This week',
    description: 'Priority tag: do this within the week.',
  },
  priorityWatch: {
    id: 'fieldTasks.priority.watch',
    defaultMessage: 'Keep looking',
    description: 'Priority tag: nothing to do yet, just keep checking.',
  },
});

interface Task {
  task: string;
  why?: string;
  timing?: string;
  priority?: string;
}

function readTasks(value: unknown): Task[] {
  return arr(value).flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) {
      return [];
    }
    const record = entry as Record<string, unknown>;
    const task = str(record.task);
    return task
      ? [
          {
            task,
            why: optStr(record.why),
            timing: optStr(record.timing),
            priority: optStr(record.priority),
          },
        ]
      : [];
  });
}

function readAvoid(value: unknown): string[] {
  return arr(value).flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) {
      return [];
    }
    const item = str((entry as Record<string, unknown>).item);
    return item ? [item] : [];
  });
}

const PRIORITY_STYLES: Record<string, string> = {
  now: 'bg-warning text-warning-foreground',
  this_week: 'bg-muted text-muted-foreground',
  watch: 'bg-transparent text-muted-foreground border border-border',
};

export const FieldTasks: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();

  const crop = str(node.props.crop);
  const stage = optStr(node.props.stage);
  const periodLabel = optStr(node.props.periodLabel);
  const tasks = readTasks(node.props.tasks);
  const avoid = readAvoid(node.props.avoid);
  const watchFor = optStr(node.props.watchFor);

  const priorityLabel = (priority: string): string =>
    priority === 'now'
      ? intl.formatMessage(messages.priorityNow)
      : priority === 'this_week'
        ? intl.formatMessage(messages.priorityWeek)
        : intl.formatMessage(messages.priorityWatch);

  return (
    <section className="w-full rounded-lg border border-border bg-card p-5">
      <header className="mb-4">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <h2 className="text-xl font-bold tracking-tight text-foreground" translate="no">
            {crop}
          </h2>
          {periodLabel ? (
            <span className="text-xs font-semibold uppercase tracking-caps text-muted-foreground">
              {periodLabel}
            </span>
          ) : null}
        </div>
        {stage ? <p className="mt-1 text-sm text-muted-foreground">{stage}</p> : null}
      </header>

      {tasks.length > 0 ? (
        <>
          <h3 className="mb-2.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-caps text-muted-foreground">
            <ListChecks className="size-4 shrink-0" aria-hidden />
            {intl.formatMessage(messages.toDo)}
          </h3>
          <ul className="space-y-3">
            {tasks.map((task) => (
              <li key={task.task} className="border-l-2 border-primary/40 pl-3.5">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <p className="min-w-0 text-base font-semibold leading-snug text-foreground">
                    {task.task}
                  </p>
                  {task.priority ? (
                    <span
                      className={`shrink-0 rounded-sm px-1.5 py-0.5 text-xs font-bold uppercase tracking-caps ${
                        PRIORITY_STYLES[task.priority] ?? PRIORITY_STYLES.watch
                      }`}
                    >
                      {priorityLabel(task.priority)}
                    </span>
                  ) : null}
                </div>
                {task.timing ? (
                  <p className="mt-0.5 text-sm font-semibold text-foreground">{task.timing}</p>
                ) : null}
                {task.why ? (
                  <p className="mt-0.5 text-sm leading-snug text-muted-foreground">{task.why}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {avoid.length > 0 ? (
        <div className="mt-5 rounded-md bg-muted p-3.5">
          <h3 className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-caps text-muted-foreground">
            <Ban className="size-4 shrink-0" aria-hidden />
            {intl.formatMessage(messages.avoid)}
          </h3>
          <ul className="space-y-1">
            {avoid.map((item) => (
              <li key={item} className="text-sm leading-snug text-foreground">
                {item}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {watchFor ? (
        <p className="mt-4 flex items-start gap-2 border-t border-border pt-3.5 text-sm leading-relaxed text-foreground">
          <Eye className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0">
            <span className="font-semibold">{intl.formatMessage(messages.watch)}: </span>
            {watchFor}
          </span>
        </p>
      ) : null}
    </section>
  );
};
